'use client';

import { useState, type FormEvent } from 'react';

import type { Cart } from '@/entities/cart';
import { useApplyPromo, useRemovePromo } from '@/entities/cart/client';
import { ApiError } from '@/shared/api';
import { formatMoney } from '@/shared/lib/money';
import { Icon } from '@/shared/ui/icon';
import { TextButton } from '@/shared/ui/text-link';

import styles from './promo-code.module.scss';

type Status = { tone: 'ok' | 'error' | 'muted'; text: string } | null;

/** Promo code block of the cart summary: apply, conditions not met, invalid, remove. */
export function PromoCode({ cart }: { cart: Cart }) {
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<Status>(null);
  const apply = useApplyPromo();
  const remove = useRemovePromo();
  const invalid = status?.tone === 'error';
  // After a reload the applied promo still shows its message (04B: the message follows the cart state).
  const shown: Status =
    status ??
    (cart.promo
      ? { tone: 'ok', text: `Промокод применён — скидка ${formatMoney(cart.promo.amount)}` }
      : null);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = code.trim();
    if (!value || apply.isPending) return;
    apply.mutate(
      { code: value },
      {
        onSuccess: (next) => {
          setStatus(
            next.promo
              ? { tone: 'ok', text: `Промокод применён — скидка ${formatMoney(next.promo.amount)}` }
              : null,
          );
          setCode('');
        },
        onError: (error) => {
          setCode(value.toUpperCase());
          setStatus({
            tone: 'error',
            text: error instanceof ApiError ? error.message : 'Не удалось применить промокод',
          });
        },
      },
    );
  };
  const drop = () => {
    remove.mutate(undefined, {
      onSuccess: () => {
        setStatus({ tone: 'muted', text: 'Промокод удалён' });
        setCode('');
      },
    });
  };

  return (
    <div className={styles.root}>
      <label htmlFor="promo" className={styles.label}>
        Промокод
      </label>
      {cart.promo ? (
        <div className={styles.applied}>
          <span className={styles.code}>
            <Icon name="check" size={16} strokeWidth={1.6} className={styles.check} />
            {cart.promo.code} · −{formatMoney(cart.promo.amount)}
          </span>
          <TextButton
            tone="muted"
            onClick={drop}
            aria-label="Удалить промокод"
            disabled={remove.isPending}
          >
            Удалить
          </TextButton>
        </div>
      ) : (
        <form className={styles.form} onSubmit={submit}>
          <input
            id="promo"
            className={styles.input}
            type="text"
            autoComplete="off"
            placeholder="Введите промокод"
            value={code}
            data-invalid={invalid || undefined}
            aria-invalid={invalid || undefined}
            aria-describedby="promo-msg"
            onChange={(event) => {
              setCode(event.target.value);
              setStatus(null);
            }}
          />
          <button
            type="submit"
            className={styles.submit}
            aria-disabled={!code.trim() || undefined}
            aria-busy={apply.isPending || undefined}
          >
            Применить
          </button>
        </form>
      )}
      <output id="promo-msg" className={styles.message} data-tone={shown?.tone}>
        {shown?.text ?? ''}
      </output>
    </div>
  );
}
